import { createSlice } from '@reduxjs/toolkit';
import MojitoCore from 'mojito/core';
import ServicesTypes from 'services/common/types.js';
import channelFactory from 'services/common/content/content-channel-factory';
import TournamentsDescriptor from './descriptors.js';
import tournamentsProvider from 'services/sports-content/content-provider/tournament-provider';

const reduxInstance = MojitoCore.Services.redux;
const { actionsRegistry } = MojitoCore.Services.Content;
const { TOURNAMENT } = TournamentsDescriptor.DATA_TYPES;
const { UNKNOWN, AVAILABLE, UNAVAILABLE } = ServicesTypes.CONTENT_STATE;

export const getTournamentsChannel = () =>
    channelFactory.getChannel(tournamentsProvider, TOURNAMENT);

/**
 * The name of the tournaments state property. Will be used to register in global redux store.
 *
 * @constant
 * @type {string}
 * @memberof Mojito.Services.SportsContent.Tournaments
 */
export const STORE_KEY = 'tournamentsStore';

export const initialState = {
    tournaments: {},
    tournamentsState: {},
};

export const { reducer, actions } = createSlice({
    name: 'tournaments',
    initialState,
    reducers: {
        updateTournament(state, { payload }) {
            const { tournamentId, tournament } = payload;

            if (tournament) {
                state.tournaments[tournamentId] = tournament;
                state.tournamentsState[tournamentId] = AVAILABLE;
            } else {
                delete state.tournaments[tournamentId];
                state.tournamentsState[tournamentId] = UNAVAILABLE;
            }
        },
        removeTournament(state, { payload }) {
            const tournamentIds = Array.isArray(payload) ? payload : [payload];

            tournamentIds.filter(Boolean).forEach(tournamentId => {
                delete state.tournaments[tournamentId];
                state.tournamentsState[tournamentId] = UNKNOWN;
            });
        },
        reset() {
            return { ...initialState };
        },
    },
});

/**
 * Subscribe to tournament.
 *
 * @function subscribe
 *
 * @param {{clientId: string, tournamentId: string}} payload - Subscription payload.
 * @returns {Mojito.Core.Services.redux.ThunkFunction} Subscribe to tournament thunk.
 * @memberof Mojito.Services.SportsContent.Tournaments.actions
 */
actions.subscribe =
    ({ clientId, tournamentId }) =>
    dispatch => {
        const onData = (id, tournament) => {
            dispatch(actions.updateTournament({ tournamentId: id, tournament }));
        };
        getTournamentsChannel().subscribe([tournamentId], clientId, onData);
    };

/**
 * Unsubscribe from tournaments.
 *
 * @function unsubscribe
 *
 * @param {string} clientId - The clientId of subscriber to unsubscribe.
 * @returns {Mojito.Core.Services.redux.ThunkFunction} Unsubscribe from tournaments thunk.
 * @memberof Mojito.Services.SportsContent.Tournaments.actions
 */
actions.unsubscribe = clientId => dispatch => {
    getTournamentsChannel().unsubscribeAll(clientId, tournamentId => {
        dispatch(actions.removeTournament(tournamentId));
    });
};

actionsRegistry.addSubscription(TOURNAMENT, actions.subscribe, actions.unsubscribe);

reduxInstance.injectReducer(STORE_KEY, reducer);
